import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { API, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
    ArrowLeft, Loader2, Crown, Timer, Zap, AlertCircle, Coins,
    MessageCircle, Send, RefreshCw, LogOut,
} from "lucide-react";

const CHIPS_PER_CREDIT = 1;
const QUICK_EMOTES = ["🔥", "😂", "💀", "👏", "🤔", "😱", "🎉", "❤️"];

const SUIT_CHAR = { s: "\u2660", h: "\u2665", d: "\u2666", c: "\u2663" };
const SUIT_COLOR = { s: "#0A0A0A", c: "#0A0A0A", h: "#E11D48", d: "#E11D48" };

function Card({ card, hidden, tiny }) {
    const size = tiny ? "w-8 h-11 text-xs" : "w-11 h-16 text-lg sm:w-14 sm:h-20 sm:text-xl";
    if (!card || hidden || card === "??") {
        return (
            <div className={`${size} bg-vibrantblue border-[3px] border-ink rounded-lg shadow-brutal flex items-center justify-center`}>
                <div className="w-full h-full bg-[repeating-linear-gradient(45deg,#2563EB,#2563EB_4px,#1E40AF_4px,#1E40AF_8px)] rounded-md" />
            </div>
        );
    }
    const r = card[0], s = card[1];
    const rank = r === "T" ? "10" : r;
    return (
        <div className={`${size} bg-white border-[3px] border-ink rounded-lg shadow-brutal flex flex-col items-center justify-center font-heading font-black`} style={{ color: SUIT_COLOR[s] }}>
            <div className="leading-none">{rank}</div>
            <div className="leading-none">{SUIT_CHAR[s]}</div>
        </div>
    );
}

function chips(n) {
    if (n == null) return "0";
    const v = n / CHIPS_PER_CREDIT;
    return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function labelAction(la) {
    if (!la) return "";
    const m = { fold: "folded", check: "checked", call: "called", bet: "bet", raise: "raised to", all_in: "all-in" };
    const l = m[la.action] || la.action;
    return la.amount != null ? `${l} ${chips(la.amount)}` : l;
}

function SeatPill({ p, isActor, isDealer, isYou }) {
    if (!p) return <div className="w-32" />;
    const busted = p.busted;
    const folded = p.folded;
    return (
        <div className={`relative brutal-card p-2 ${busted ? "bg-ink text-white/60" : folded ? "bg-cream/60 opacity-60" : "bg-white"} ${isActor ? "ring-4 ring-vibrantorange" : ""}`} style={{ minWidth: 128 }}>
            <div className="flex items-center gap-1.5 mb-1">
                <span className="font-heading font-bold text-xs truncate">{p.username}{isYou ? " (you)" : ""}</span>
                {isDealer && <span className="ml-auto w-5 h-5 rounded-full bg-ink text-white text-[10px] font-heading font-black flex items-center justify-center">D</span>}
            </div>
            <div className="flex items-center gap-1">
                {p.hole && p.hole.length > 0 && (
                    <div className="flex gap-0.5">
                        <Card card={p.hole[0]} hidden={p.hole[0] === "??"} tiny />
                        <Card card={p.hole[1]} hidden={p.hole[1] === "??"} tiny />
                    </div>
                )}
                <div className="flex-1 text-right">
                    {busted ? (
                        <span className="font-heading font-bold text-[10px] uppercase tracking-widest">OUT · #{p.place}</span>
                    ) : (
                        <>
                            <div className="font-heading font-black text-sm text-ink tabular-nums" data-testid={`seat-chips-${p.username}`}>{chips(p.chips)}</div>
                            {p.bet > 0 && <div className="font-heading font-bold text-xs text-vibrantorange">bet {chips(p.bet)}</div>}
                        </>
                    )}
                </div>
            </div>
            {p.all_in && !busted && <div className="mt-1 text-center bg-vibrantpink text-white font-heading font-black text-[10px] uppercase tracking-widest rounded">ALL-IN</div>}
        </div>
    );
}

export default function PokerTournamentTable() {
    const { tid } = useParams();
    const { user } = useAuth();
    const [state, setState] = useState(null);
    const [connected, setConnected] = useState(false);
    const [err, setErr] = useState("");
    const [raiseAmt, setRaiseAmt] = useState(0);
    const [now, setNow] = useState(Date.now());
    const [chatMessages, setChatMessages] = useState([]);
    const [chatOpen, setChatOpen] = useState(false);
    const [chatText, setChatText] = useState("");
    const [rebuyAmt, setRebuyAmt] = useState(0);
    const [rebuyBusy, setRebuyBusy] = useState(false);
    const [rebuyErr, setRebuyErr] = useState("");
    const [cashoutBusy, setCashoutBusy] = useState(false);
    const [cashoutErr, setCashoutErr] = useState("");
    const [cashoutResult, setCashoutResult] = useState(null);
    const [cashoutConfirm, setCashoutConfirm] = useState(false);
    const wsRef = useRef(null);
    const chatEndRef = useRef(null);

    useEffect(() => {
        const i = setInterval(() => setNow(Date.now()), 500);
        return () => clearInterval(i);
    }, []);

    useEffect(() => {
        if (!user || user === false) return;
        const token = localStorage.getItem("vp_token") || localStorage.getItem("endfb_token");
        if (!token) return;
        const wsBase = API.replace(/^http/, "ws");
        const url = `${wsBase}/poker/tournaments/${tid}/ws?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => setConnected(true);
        ws.onclose = () => setConnected(false);
        ws.onmessage = (e) => {
            try {
                const msg = JSON.parse(e.data);
                if (msg.type === "state") {
                    setState(msg);
                    if (msg.chat) setChatMessages(msg.chat);
                    // Auto-set rebuy amt to buy-in if we just busted
                    if (msg.game && msg.you_seat >= 0) {
                        const me = msg.game.players.find((p) => p.seat === msg.you_seat);
                        const meP = msg.tournament?.players?.find((p) => p.user_id === user?.id);
                        if (me?.busted && meP && !rebuyAmt) setRebuyAmt(meP.buy_in);
                    }
                } else if (msg.type === "action_error") {
                    setErr(msg.error);
                } else if (msg.type === "chat") {
                    setChatMessages((c) => [...c, msg.message].slice(-60));
                    if (!chatOpen) {
                        // brief flash: reopen a moment
                    }
                }
            } catch {}
        };
        return () => { try { ws.close(); } catch {} };
    }, [tid, user]);

    const send = (action, amount) => {
        setErr("");
        try {
            wsRef.current?.send(JSON.stringify({ type: "action", action, ...(amount != null ? { amount } : {}) }));
        } catch (e) {
            setErr(e.message);
        }
    };

    const sendChat = (text, emote) => {
        try {
            wsRef.current?.send(JSON.stringify({ type: "chat", text: text || "", emote: emote || "" }));
        } catch {}
    };

    const submitChat = () => {
        const t = chatText.trim();
        if (!t) return;
        sendChat(t, "");
        setChatText("");
    };

    const doRebuy = async () => {
        if (!state?.tournament) return;
        const amt = parseInt(rebuyAmt) || 0;
        if (amt < 3 || amt > 10000) {
            setRebuyErr("Buy-in must be 3-10000 credits");
            return;
        }
        setRebuyErr("");
        setRebuyBusy(true);
        try {
            await api.post(`/poker/tournaments/${tid}/rebuy`, { buy_in: amt });
        } catch (e) {
            setRebuyErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setRebuyBusy(false);
        }
    };

    const doCashout = async () => {
        setCashoutErr("");
        setCashoutBusy(true);
        try {
            const { data } = await api.post(`/poker/tournaments/${tid}/cashout`);
            setCashoutResult(data);
            setCashoutConfirm(false);
        } catch (e) {
            setCashoutErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setCashoutBusy(false);
        }
    };

    useEffect(() => {
        if (chatEndRef.current) chatEndRef.current.scrollIntoView({ block: "end" });
    }, [chatMessages, chatOpen]);

    if (!user || user === false) {
        return (
            <div className="max-w-3xl mx-auto p-8 text-center">
                <p className="font-heading font-bold text-ink/60">Please <Link to="/login" className="text-vibrantorange underline">log in</Link> to view the table.</p>
            </div>
        );
    }

    if (!state) {
        return (
            <div className="max-w-3xl mx-auto p-8 text-center">
                <Loader2 className="w-6 h-6 mx-auto animate-spin text-vibrantorange" strokeWidth={2.5} />
                <p className="mt-2 font-heading font-bold text-ink/60">Connecting to table…</p>
            </div>
        );
    }

    const t = state.tournament;
    const g = state.game;
    const you = g ? g.players.find((p) => p.seat === state.you_seat) : null;
    const la = g?.legal_actions || { actor: false };
    const isMyTurn = la.actor;
    const secondsLeft = state.action_deadline ? Math.max(0, Math.round((state.action_deadline * 1000 - now) / 1000)) : 0;

    // Seat layout: arrange players around a table with `you` at bottom.
    const players = g ? g.players : [];
    const n = players.length;
    const youIdx = state.you_seat >= 0 ? state.you_seat : 0;
    const orderedSeats = [];
    for (let i = 0; i < n; i++) orderedSeats.push(players[(youIdx + i) % n]);
    const layoutBySize = {
        2: [[50, 90], [50, 10]],
        3: [[50, 90], [15, 30], [85, 30]],
        4: [[50, 90], [15, 50], [50, 10], [85, 50]],
        5: [[50, 90], [12, 60], [30, 12], [70, 12], [88, 60]],
        6: [[50, 92], [10, 65], [20, 15], [80, 15], [90, 65], [50, 8]],
    };
    const layout = layoutBySize[n] || layoutBySize[6];

    const finished = g?.finished;

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
            <Link to="/poker/tournaments" data-testid="table-back-link" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
                Back to lobby
            </Link>

            <div className="brutal-card p-4 sm:p-6 bg-emerald-700 mb-4 relative" style={{ minHeight: 460 }}>
                {/* Header row */}
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2 text-white">
                    <div className="flex items-center gap-3">
                        <span className="font-heading font-black text-lg tracking-tighter">6-Max Tournament</span>
                        <span className="flex items-center gap-1 font-heading font-bold text-xs bg-white/10 border-[2px] border-white/40 rounded px-2 py-0.5">
                            <Zap className="w-3.5 h-3.5" strokeWidth={2.5} /> lvl {(t.blind_level ?? 0) + 1} · {t.blinds[0] / CHIPS_PER_CREDIT}/{t.blinds[1] / CHIPS_PER_CREDIT}
                        </span>
                        <span className="flex items-center gap-1 font-heading font-bold text-xs bg-white/10 border-[2px] border-white/40 rounded px-2 py-0.5">
                            <Coins className="w-3.5 h-3.5" strokeWidth={2.5} /> pool {t.prize_pool}
                        </span>
                        {t.players_remaining != null && (
                            <span className="flex items-center gap-1 font-heading font-bold text-xs bg-white/10 border-[2px] border-white/40 rounded px-2 py-0.5">
                                {t.players_remaining} left{t.tables_count > 1 ? ` · ${t.tables_count} tables` : ""}
                            </span>
                        )}
                    </div>
                    {!finished && (
                        <span data-testid="action-clock" className={`flex items-center gap-1 font-heading font-black text-lg tabular-nums ${secondsLeft <= 5 ? "text-vibrantpink" : "text-white"}`}>
                            <Timer className="w-4 h-4" strokeWidth={2.5} /> {secondsLeft}s
                        </span>
                    )}
                </div>

                {/* Community + pot */}
                <div className="flex flex-col items-center justify-center py-8">
                    <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-white/70 mb-1">Pot</div>
                    <div data-testid="table-pot" className="font-heading font-black text-3xl text-white tabular-nums mb-4">{chips(g?.pot || 0)}</div>
                    <div className="flex gap-1.5">
                        {(g?.community || []).map((c, i) => <Card key={i} card={c} />)}
                        {Array.from({ length: 5 - (g?.community?.length || 0) }).map((_, i) => (
                            <div key={`ph${i}`} className="w-11 h-16 sm:w-14 sm:h-20 bg-emerald-800/60 border-2 border-white/30 border-dashed rounded-lg" />
                        ))}
                    </div>
                    {g?.last_action && (
                        <div className="mt-3 font-heading font-bold text-xs text-white/90 bg-black/30 rounded px-3 py-1">
                            {g.last_action.username || "player"} {labelAction(g.last_action)}
                        </div>
                    )}
                </div>

                {/* Seat pills positioned around table (absolute on md+) */}
                <div className="hidden md:block relative w-full" style={{ height: 220 }}>
                    {orderedSeats.map((p, i) => {
                        const [x, y] = layout[i];
                        return (
                            <div key={`${p.seat}`} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${x}%`, top: `${y}%` }}>
                                <SeatPill p={p} isActor={g?.current_actor === p.seat} isDealer={g?.dealer === p.seat} isYou={p.seat === state.you_seat} />
                            </div>
                        );
                    })}
                </div>
                {/* Mobile: stacked list */}
                <div className="md:hidden grid grid-cols-2 gap-2 mt-2">
                    {orderedSeats.map((p) => (
                        <SeatPill key={p.seat} p={p} isActor={g?.current_actor === p.seat} isDealer={g?.dealer === p.seat} isYou={p.seat === state.you_seat} />
                    ))}
                </div>

                {!connected && (
                    <div className="absolute top-2 right-2 bg-vibrantpink/90 text-white text-xs font-heading font-bold px-2 py-1 rounded">
                        reconnecting…
                    </div>
                )}
            </div>

            {/* Action bar */}
            {you && !you.busted && !finished && (
                <div data-testid="action-bar" className={`brutal-card p-4 bg-white ${isMyTurn ? "ring-4 ring-vibrantorange" : "opacity-60"}`}>
                    {isMyTurn ? (
                        <>
                            <div className="flex flex-wrap gap-2 items-stretch">
                                <button
                                    type="button"
                                    onClick={() => send("fold")}
                                    data-testid="btn-fold"
                                    disabled={!la.can_fold}
                                    className="brutal-btn bg-ink text-white text-sm py-2 px-4"
                                >Fold</button>
                                {la.can_check && (
                                    <button
                                        type="button"
                                        onClick={() => send("check")}
                                        data-testid="btn-check"
                                        className="brutal-btn bg-emerald-600 text-white text-sm py-2 px-4"
                                    >Check</button>
                                )}
                                {la.can_call && (
                                    <button
                                        type="button"
                                        onClick={() => send("call")}
                                        data-testid="btn-call"
                                        className="brutal-btn bg-vibrantblue text-white text-sm py-2 px-4"
                                    >Call {chips(la.to_call)}</button>
                                )}
                                {(la.can_bet || la.can_raise) && (
                                    <div className="flex gap-1 items-stretch">
                                        <input
                                            type="number"
                                            min={la.min_raise_to}
                                            max={la.max_raise_to}
                                            value={raiseAmt || la.min_raise_to}
                                            onChange={(e) => setRaiseAmt(parseInt(e.target.value) || 0)}
                                            data-testid="raise-amount-input"
                                            className="w-24 bg-cream border-[3px] border-ink rounded-xl px-2 font-heading font-black text-lg text-ink text-center focus:outline-none"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => send(la.can_raise ? "raise" : "bet", raiseAmt || la.min_raise_to)}
                                            data-testid="btn-raise"
                                            className="brutal-btn bg-vibrantorange text-white text-sm py-2 px-4"
                                        >{la.can_raise ? "Raise to" : "Bet"}</button>
                                    </div>
                                )}
                                {la.can_all_in && (
                                    <button
                                        type="button"
                                        onClick={() => send("all_in")}
                                        data-testid="btn-all-in"
                                        className="brutal-btn bg-vibrantpink text-white text-sm py-2 px-4"
                                    >All-in</button>
                                )}
                            </div>
                            {err && (
                                <div className="mt-2 flex items-start gap-2 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink rounded-xl p-2">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                                    <span className="font-body font-semibold text-xs">{err}</span>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="font-heading font-bold text-sm text-ink/60 text-center">Waiting on other players…</p>
                    )}
                </div>
            )}

            {/* Early cashout panel — visible for alive players once tournament has started */}
            {you && !you.busted && !finished && t.status === "RUNNING" && (() => {
                const mePlayer = t.players?.find((pp) => pp.user_id === user.id);
                if (!mePlayer || mePlayer.cashed_out) return null;
                const unlockAtMs = t.cashout_available_at ? t.cashout_available_at * 1000 : 0;
                const msLeft = Math.max(0, unlockAtMs - now);
                const unlocked = t.cashout_open && msLeft === 0;
                const penaltyPct = Math.round((t.cashout_penalty_pct ?? 0.04) * 100);
                const myChips = you.chips || 0;
                const est = Math.floor(myChips * (1 - (t.cashout_penalty_pct ?? 0.04)));
                if (cashoutResult) {
                    return (
                        <div data-testid="cashout-success" className="brutal-card p-4 mt-3 bg-emerald-700 text-white">
                            <p className="font-heading font-black text-lg tracking-tighter">Cashed out — +{cashoutResult.credits_paid}c to your wallet</p>
                            <p className="font-body text-xs text-white/80 mt-1">{cashoutResult.penalty_credits}c stayed in the prize pool for the winner.</p>
                        </div>
                    );
                }
                return (
                    <div data-testid="cashout-panel" className="brutal-card p-3 mt-3 bg-cream">
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex-1 min-w-[180px]">
                                <div className="font-heading font-black text-sm text-ink tracking-tighter">Early cashout</div>
                                <div className="font-body text-[11px] text-ink/60 mt-0.5">
                                    {unlocked
                                        ? <>Cash out now for <span className="font-heading font-bold text-ink">{est}c</span> ({penaltyPct}% stays in prize pool)</>
                                        : <>Unlocks in <span className="font-heading font-bold tabular-nums text-ink" data-testid="cashout-countdown">{Math.floor(msLeft / 3600000)}:{String(Math.floor((msLeft % 3600000) / 60000)).padStart(2, "0")}:{String(Math.floor((msLeft % 60000) / 1000)).padStart(2, "0")}</span></>
                                    }
                                </div>
                            </div>
                            {cashoutConfirm ? (
                                <div className="flex gap-2 items-stretch">
                                    <button
                                        type="button"
                                        onClick={() => setCashoutConfirm(false)}
                                        disabled={cashoutBusy}
                                        data-testid="cashout-cancel-btn"
                                        className="brutal-btn bg-white text-ink text-xs py-2 px-3 disabled:opacity-50"
                                    >Cancel</button>
                                    <button
                                        type="button"
                                        onClick={doCashout}
                                        disabled={cashoutBusy}
                                        data-testid="cashout-confirm-btn"
                                        className="brutal-btn bg-vibrantpink text-white text-xs py-2 px-3 disabled:opacity-50"
                                    >
                                        {cashoutBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.5} /> : `Confirm — take ${est}c`}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => { setCashoutErr(""); setCashoutConfirm(true); }}
                                    disabled={!unlocked}
                                    data-testid="cashout-btn"
                                    className="brutal-btn bg-vibrantpink text-white text-xs py-2 px-3 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <LogOut className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> Cashout & Leave
                                </button>
                            )}
                        </div>
                        {cashoutErr && (
                            <div data-testid="cashout-error" className="mt-2 flex items-start gap-2 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink rounded-xl p-2">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                                <span className="font-body font-semibold text-xs">{cashoutErr}</span>
                            </div>
                        )}
                    </div>
                );
            })()}

            {you && you.busted && !finished && (() => {
                const mePlayer = t.players?.find((pp) => pp.user_id === user.id);
                const rebuyOpen = t.rebuy_window_open;
                const rebuyDeadlineMs = t.rebuy_deadline ? t.rebuy_deadline * 1000 : 0;
                const rebuyMsLeft = Math.max(0, rebuyDeadlineMs - now);
                const canRebuy = rebuyOpen && mePlayer && !mePlayer.rebuy_used && !mePlayer.cashed_out;
                if (mePlayer?.cashed_out) {
                    return (
                        <div data-testid="cashed-out-notice" className="brutal-card p-5 bg-emerald-700 text-white">
                            <p className="font-heading font-black text-xl tracking-tighter">You cashed out — +{mePlayer.cashout_credits}c refunded to your wallet</p>
                            <p className="font-body text-sm text-white/70 mt-1">You're out of the tournament. Good luck to the survivors!</p>
                        </div>
                    );
                }
                return (
                    <div data-testid="bust-notice" className="brutal-card p-5 bg-ink text-white">
                        <p className="font-heading font-black text-xl tracking-tighter">You busted — finishing place #{you.place}</p>
                        <p className="font-body text-sm text-white/70 mt-1 mb-3">
                            {canRebuy
                                ? `Rebuy window closes in ${Math.floor(rebuyMsLeft / 60000)}:${String(Math.floor((rebuyMsLeft % 60000) / 1000)).padStart(2, "0")} — one shot only.`
                                : mePlayer?.rebuy_used
                                    ? "You've already used your rebuy — good luck to the survivors."
                                    : "Rebuy window has closed. Stay to watch the finish."}
                        </p>
                        {canRebuy && (
                            <div className="flex gap-2 items-stretch">
                                <input
                                    type="number"
                                    min={3}
                                    max={10000}
                                    value={rebuyAmt}
                                    onChange={(e) => setRebuyAmt(Math.max(3, Math.min(10000, parseInt(e.target.value) || 3)))}
                                    data-testid="rebuy-amount-input"
                                    className="w-28 bg-white border-[3px] border-white rounded-xl px-2 py-2 font-heading font-black text-lg text-ink text-center focus:outline-none"
                                />
                                <button
                                    type="button"
                                    onClick={doRebuy}
                                    disabled={rebuyBusy}
                                    data-testid="rebuy-btn"
                                    className="brutal-btn bg-vibrantorange text-white text-sm py-2 px-4 disabled:opacity-50"
                                >
                                    {rebuyBusy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : <><RefreshCw className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Rebuy</>}
                                </button>
                            </div>
                        )}
                        {rebuyErr && (
                            <div data-testid="rebuy-error" className="mt-2 flex items-start gap-2 bg-vibrantpink/25 border-[3px] border-vibrantpink text-white rounded-xl p-2">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                                <span className="font-body font-semibold text-xs">{rebuyErr}</span>
                            </div>
                        )}
                    </div>
                );
            })()}

            {finished && t.payouts && (
                <div data-testid="tournament-result" className="brutal-card p-5 bg-vibrantyellow">
                    <h2 className="font-heading font-black text-2xl tracking-tighter text-ink mb-3">Tournament complete!</h2>
                    <div className="space-y-1">
                        {t.payouts.map((pay) => (
                            <div key={pay.place} className="flex items-center gap-2 font-body text-sm">
                                <Crown className={`w-4 h-4 ${pay.place === 1 ? "text-vibrantorange" : pay.place === 2 ? "text-slate-500" : "text-orange-800"}`} strokeWidth={2.5} fill="currentColor" />
                                <span className="font-heading font-bold text-ink">{pay.place}. {pay.username}</span>
                                <span className="ml-auto text-vibrantorange font-heading font-bold">+{pay.credits}c</span>
                            </div>
                        ))}
                    </div>
                    <Link to="/poker/tournaments" className="brutal-btn-primary mt-4 inline-flex text-sm py-2 px-4">Back to lobby</Link>
                </div>
            )}

            {/* Floating chat bubble + panel */}
            <div className="fixed bottom-4 right-4 z-40" data-testid="table-chat">
                {chatOpen ? (
                    <div className="brutal-card bg-white p-3 flex flex-col" style={{ width: 300, height: 380 }}>
                        <div className="flex items-center justify-between border-b-[2px] border-ink/10 pb-2 mb-2">
                            <span className="font-heading font-black text-sm text-ink">Table chat</span>
                            <button type="button" onClick={() => setChatOpen(false)} data-testid="chat-close-btn" className="text-ink/50 hover:text-ink">
                                <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto space-y-1 pr-1" data-testid="chat-messages">
                            {chatMessages.length === 0 ? (
                                <p className="font-body text-xs text-ink/40 text-center mt-4">No messages yet — break the ice.</p>
                            ) : (
                                chatMessages.map((m) => (
                                    <div key={m.id} className="flex gap-1.5 items-baseline">
                                        <span className={`font-heading font-bold text-[11px] ${m.user_id === user?.id ? "text-vibrantorange" : "text-vibrantblue"}`}>
                                            {m.username}:
                                        </span>
                                        <span className="font-body text-xs text-ink break-words">
                                            {m.emote ? <span className="text-lg">{m.emote}</span> : m.text}
                                        </span>
                                    </div>
                                ))
                            )}
                            <div ref={chatEndRef} />
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2 border-t-[2px] border-ink/10 pt-2" data-testid="chat-emotes">
                            {QUICK_EMOTES.map((e) => (
                                <button
                                    key={e}
                                    type="button"
                                    onClick={() => sendChat("", e)}
                                    data-testid={`chat-emote-${e}`}
                                    className="w-8 h-8 bg-cream border-[2px] border-ink rounded-lg hover:-translate-y-0.5 transition-all text-base flex items-center justify-center"
                                >{e}</button>
                            ))}
                        </div>
                        <div className="flex gap-1 mt-2">
                            <input
                                type="text"
                                value={chatText}
                                onChange={(e) => setChatText(e.target.value.slice(0, 200))}
                                onKeyDown={(e) => e.key === "Enter" && submitChat()}
                                placeholder="Say something…"
                                data-testid="chat-input"
                                className="flex-1 bg-cream border-[3px] border-ink rounded-xl px-2 py-1.5 font-body text-xs focus:outline-none"
                            />
                            <button
                                type="button"
                                onClick={submitChat}
                                disabled={!chatText.trim()}
                                data-testid="chat-send-btn"
                                className="brutal-btn-primary text-xs py-1.5 px-2 disabled:opacity-50"
                            >
                                <Send className="w-3.5 h-3.5" strokeWidth={2.5} />
                            </button>
                        </div>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => setChatOpen(true)}
                        data-testid="chat-open-btn"
                        className="w-14 h-14 bg-vibrantpink border-[3px] border-ink rounded-full shadow-brutal-lg flex items-center justify-center hover:-translate-y-0.5 transition-all relative"
                    >
                        <MessageCircle className="w-6 h-6 text-white" strokeWidth={2.5} />
                        {chatMessages.length > 0 && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-vibrantyellow border-2 border-ink rounded-full text-[10px] font-heading font-black text-ink flex items-center justify-center">
                                {chatMessages.length > 99 ? "99+" : chatMessages.length}
                            </span>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}
